import { getCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import throttle from "lodash/throttle";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        currentLanguage: state.context.session.language,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    detailsPage?: MobiusPageProps;
    alertZoneWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],

        css: css`
            position: sticky;
            top: 0;
            z-index: 20;
            padding: 0px !important;
            max-width: 100% !important;
            background-color: ${getColor("info")};
            .hidden {
                transition: all 2s;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                height: 0;
                                overflow: hidden;
                            `,
                        ],
                        "min",
                    )}
            }
            > div[class^="sc"] ~ div {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        background-color: ${COLORS.white};
                        position: sticky;
                    `,
                    null,
                ])}
        `,
    },

    container: {
        css: css`
            // background-color: #112945;
            display: flex;
            max-width: 1200px;
            margin: 0 auto;
            > div:first-child {
                flex-grow: 1;
                flex-basis: 90%;
                margin-left: 0;
            }
            > div:last-child {
                max-width: 3%;
                height: auto;
                margin-right: auto;
                > div[class^="sc"] {
                    height: 50px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: none;
                        `,
                        null,
                        css`
                            max-width: 100px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${TYPOGRAPHY.size.tiny};
            padding-left: 15px;
            display: none;
        `,
    },
    alertZoneWrapper: {
        css: css`
            height: auto;
        `
    }
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentLanguage,
    shellContext: { isInShell },
}: Props) => {
    const [navbarHidden, setNavbarHidden] = useState(false);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if(!getCookie("cms_CurrentContentMode")) {
            window.addEventListener("scroll", scrollHandler);
        }
        return () => window.removeEventListener("scroll", scrollHandler);
    }, []);

    React.useEffect(() => {
        if (!isInShell && currentLanguage?.languageCode !== getCookie("SetContextLanguageCode")) {
            window.location.reload();
        }
    });

    const scrollHandler = throttle(() => {
        const isDesktopHeader = window.outerWidth >= 992;
        if (isDesktopHeader) {
            if (document.body.scrollTop > 1 || document.documentElement.scrollTop > 1) {
                if (!navbarHidden) {
                    setNavbarHidden(true);
                }
            } else {
                setNavbarHidden(false);
            }
        }
    }, 100);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper {...styles.alertZoneWrapper}>
                <Zone contentId={id} zoneName="AlertBanner" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page} className="test" id="main-navbar">
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <StyledWrapper {...styles.container} className={navbarHidden ? "hidden" : ""}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                        <Zone contentId={id} zoneName="Cart" />
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withIsInShell(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;